/**
 * 表示倍率 (画面全体のズーム) の唯一の実装。
 *
 * ## なぜ font-size ではなく zoom なのか
 * 本文の基準は `app/globals.css` の `body { font-size: 14px }` だが、UI の大半は
 * `text-[11px]` `text-[13px]` のような **px 直書きの Tailwind クラス** (400 箇所超) で
 * 指定されている。そのためルートの font-size (rem 基準) を変えても文字は連動しない。
 * 画面全体を一様に拡大できるのは `zoom` だけなので、ここでは `zoom` を使う。
 *
 * ## Web とデスクトップで見え方がズレる理由
 * Cmd +/- は Chrome では **ブラウザ自身のページズームも同時に効く**
 * (ブラウザのズームショートカットは preventDefault で止められない) ため、
 * Web だけ「ブラウザズーム x CSS ズーム」で二重に拡大される。
 * Tauri (WKWebView) はブラウザズームが無いぶん CSS ズームしか掛からず、
 * 同じ操作をしてもデスクトップの方が小さく見える。
 * さらに保存先が localStorage だとオリジンごとに分かれる
 * (https://donezo... と tauri://localhost) ため、Web で拡大しても
 * デスクトップには一切引き継がれなかった。
 *
 * そこで倍率を `AppSettings.displayZoom` に置き、`user_settings` 経由で
 * 端末間で同期する。Web で選んだ表示サイズがデスクトップにもそのまま反映される。
 */

/** 表示倍率の下限 / 上限 / ショートカットの刻み (従来の providers.tsx と同じ値) */
export const MIN_DISPLAY_ZOOM = 0.5;
export const MAX_DISPLAY_ZOOM = 2.0;
export const DISPLAY_ZOOM_STEP = 0.1;

export const DEFAULT_DISPLAY_ZOOM = 1;

/**
 * 設定画面に出すプリセット。
 * 「デスクトップが小さく見える」への回答なので既定より大きい側を厚めに用意する。
 */
export const DISPLAY_ZOOM_PRESETS: readonly {
  value: number;
  label: string;
  description: string;
}[] = [
  { value: 0.9, label: "小", description: "90%" },
  { value: 1, label: "標準", description: "100%" },
  { value: 1.15, label: "大", description: "115%" },
  { value: 1.3, label: "特大", description: "130%" },
];

/**
 * 設定より先に (=最初のペイント前に) 倍率を復元するためのローカルミラー。
 * 正は `AppSettings.displayZoom`。ここは起動直後のちらつき防止のキャッシュにすぎない。
 */
export const DISPLAY_ZOOM_MIRROR_KEY = "app-display-zoom";

/** 旧実装 (components/providers.tsx) が使っていたキー。初回だけ引き継ぐ */
export const LEGACY_DISPLAY_ZOOM_KEY = "app-zoom-level";

/** 浮動小数の誤差 (0.1 の加算で 1.0000000000000002 等) を丸める */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

export function clampDisplayZoom(value: number): number {
  return round2(Math.min(MAX_DISPLAY_ZOOM, Math.max(MIN_DISPLAY_ZOOM, value)));
}

/** 設定値として不正・未設定なら既定 (1 = 100%) に丸める */
export function normalizeDisplayZoom(value: unknown): number {
  const parsed = typeof value === "string" ? Number.parseFloat(value) : value;
  if (typeof parsed !== "number" || !Number.isFinite(parsed) || parsed <= 0) {
    return DEFAULT_DISPLAY_ZOOM;
  }
  return clampDisplayZoom(parsed);
}

/** Cmd +/- 用。`direction` は +1 で拡大、-1 で縮小 */
export function stepDisplayZoom(current: unknown, direction: number): number {
  return clampDisplayZoom(
    normalizeDisplayZoom(current) + DISPLAY_ZOOM_STEP * Math.sign(direction)
  );
}

/** 100%, 115% のような表示用文字列 */
export function formatDisplayZoom(value: unknown): string {
  return `${Math.round(normalizeDisplayZoom(value) * 100)}%`;
}

/**
 * 実際に DOM へ反映する。倍率が既定なら inline style を消して素の状態に戻す
 * (`zoom: 1` を残すと合成レイヤが増えるだけで得がない)。
 */
export function applyDisplayZoom(value: unknown): number {
  const zoom = normalizeDisplayZoom(value);
  if (typeof document === "undefined") return zoom;

  // `zoom` は CSSOM の標準プロパティ一覧に無い (jsdom では setProperty /
  // removeProperty が効かない) ため、代入で読み書きする。
  // 空文字を入れれば実ブラウザでは宣言そのものが消える
  const root = document.documentElement;
  root.style.zoom = zoom === DEFAULT_DISPLAY_ZOOM ? "" : String(zoom);

  try {
    localStorage.setItem(DISPLAY_ZOOM_MIRROR_KEY, String(zoom));
  } catch {
    // プライベートブラウジング等で localStorage が使えなくても致命的ではない
  }
  return zoom;
}

/** いま DOM に当たっている倍率。当たっていなければミラー、それも無ければ既定 */
export function readAppliedDisplayZoom(): number {
  if (typeof document !== "undefined") {
    const inline = document.documentElement.style.zoom;
    if (inline) return normalizeDisplayZoom(inline);
  }
  return readMirroredDisplayZoom() ?? DEFAULT_DISPLAY_ZOOM;
}

/**
 * ショートカットで倍率が変わったことを知らせるイベント。
 *
 * ショートカットの受け口は全ページ共通の components/providers.tsx にあるが、
 * 設定ストア (lib/store/app-settings-store) は supabase-client を引き込むため、
 * LP や規約ページの共通チャンクに載せたくない。
 * そこで providers 側はこの軽量イベントを投げるだけにして、保存は
 * アプリ本体に載る components/display-zoom-sync.tsx が担当する。
 */
export const DISPLAY_ZOOM_CHANGE_EVENT = "donezo:display-zoom-change";

export function emitDisplayZoomChange(zoom: number): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<number>(DISPLAY_ZOOM_CHANGE_EVENT, { detail: zoom })
  );
}

/**
 * ミラー (と旧キー) から起動時の倍率を読む。
 * 設定の読み込み・同期が終わるまでの暫定値。
 */
export function readMirroredDisplayZoom(): number | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw =
      localStorage.getItem(DISPLAY_ZOOM_MIRROR_KEY) ??
      localStorage.getItem(LEGACY_DISPLAY_ZOOM_KEY);
    if (raw === null) return null;
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) && parsed > 0 ? clampDisplayZoom(parsed) : null;
  } catch {
    return null;
  }
}
